import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';
import {
    Users,
    Link2,
    Copy,
    Share2,
    TrendingUp,
    DollarSign,
    UserPlus,
    CheckCircle,
    Clock,
    AlertCircle,
    ExternalLink,
    Filter
} from 'lucide-react';
import Button from '../components/Button';

// Configuração do Supabase
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseKey);

interface AfiliadoData {
    id: string;
    corretor_id: string;
    nome_corretor: string;
    email_corretor: string;
    codigo_afiliado: string;
    link_afiliado: string;
    percentual_comissao: number;
    status: string;
    total_indicacoes: number;
    total_conversoes: number;
    total_comissoes: number;
    data_cadastro: string;
    indicacoes_mes_atual: number;
    conversoes_mes_atual: number;
    comissoes_mes_atual: number;
    indicacoes_mes_anterior: number;
    conversoes_mes_anterior: number;
    comissoes_mes_anterior: number;
}

interface IndicacaoData {
    id: string;
    afiliado_id: string;
    codigo_afiliado: string;
    nome_afiliado: string;
    email_indicado: string;
    nome_indicado?: string;
    telefone_indicado?: string;
    status: string;
    data_indicacao: string;
    data_conversao?: string;
    valor_comissao: number;
    observacoes?: string;
    nome_corretor_indicado?: string;
    email_corretor_indicado?: string;
}

export default function Afiliados() {
    const [afiliadoData, setAfiliadoData] = useState<AfiliadoData | null>(null);
    const [indicacoes, setIndicacoes] = useState<IndicacaoData[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [copied, setCopied] = useState(false);
    const [filterStatus, setFilterStatus] = useState('todos');

    useEffect(() => {
        loadAfiliadoData();
    }, []);

    useEffect(() => {
        if (afiliadoData) {
            loadIndicacoes();
        }
    }, [afiliadoData, filterStatus]);

    const loadAfiliadoData = async () => {
        try {
            setLoading(true);
            setError('');

            // Obter usuário logado
            const { data: { user }, error: userError } = await supabase.auth.getUser();
            console.log('Usuário logado:', user);
            console.log('Erro de usuário:', userError);

            if (userError) {
                console.error('Erro de autenticação:', userError);
                setError('Erro de autenticação: ' + userError.message);
                return;
            }

            if (!user) {
                setError('Usuário não autenticado');
                return;
            }

            // Buscar corretor do usuário logado
            const { data: corretor, error: corretorError } = await supabase
                .from('corretores')
                .select('id, nome, email')
                .eq('user_id', user.id)
                .single();

            console.log('Corretor encontrado:', corretor);
            console.log('Erro do corretor:', corretorError);

            if (corretorError) {
                console.error('Erro ao buscar corretor:', corretorError);
                setError('Erro ao buscar corretor: ' + corretorError.message);
                return;
            }

            if (!corretor) {
                setError('Corretor não encontrado para este usuário');
                return;
            }

            // Buscar ou criar afiliado
            let { data: afiliado, error: afiliadoError } = await supabase
                .from('afiliados')
                .select('*')
                .eq('corretor_id', corretor.id)
                .single();

            console.log('Afiliado encontrado:', afiliado);
            console.log('Erro do afiliado:', afiliadoError);

            // Se não existe afiliado, criar um diretamente
            if (afiliadoError && afiliadoError.code === 'PGRST116') {
                console.log('Criando novo afiliado diretamente...');

                try {
                    // Gerar código único de afiliado
                    const codigo = Math.random().toString(36).substring(2, 10).toUpperCase();
                    const linkAfiliado = `https://app.latera.com.br/cadastro?ref=${codigo}`;

                    const { data: novoAfiliado, error: createError } = await supabase
                        .from('afiliados')
                        .insert({
                            corretor_id: corretor.id,
                            codigo_afiliado: codigo,
                            link_afiliado: linkAfiliado,
                            percentual_comissao: 10.00,
                            status: 'ativo'
                        })
                        .select()
                        .single();

                    console.log('Novo afiliado criado:', novoAfiliado);
                    console.log('Erro ao criar:', createError);

                    if (createError) {
                        console.error('Erro ao criar afiliado:', createError);
                        setError('Erro ao criar perfil de afiliado: ' + createError.message);
                        return;
                    }

                    if (!novoAfiliado) {
                        setError('Afiliado criado não encontrado');
                        return;
                    }

                    afiliado = novoAfiliado;
                } catch (directCreateError) {
                    console.error('Erro ao criar afiliado diretamente:', directCreateError);
                    setError('Erro ao criar perfil de afiliado: ' + (directCreateError instanceof Error ? directCreateError.message : 'Erro desconhecido'));
                    return;
                }
            } else if (afiliadoError) {
                console.error('Erro ao buscar afiliado:', afiliadoError);

                // Se for erro de permissão, tentar criar afiliado diretamente
                if (afiliadoError.message.includes('permission denied') || afiliadoError.message.includes('RLS')) {
                    console.log('Tentando criar afiliado diretamente devido a problema de RLS...');

                    try {
                        const codigo = Math.random().toString(36).substring(2, 10).toUpperCase();
                        const linkAfiliado = `https://app.latera.com.br/cadastro?ref=${codigo}`;

                        const { data: novoAfiliado, error: createError } = await supabase
                            .from('afiliados')
                            .insert({
                                corretor_id: corretor.id,
                                codigo_afiliado: codigo,
                                link_afiliado: linkAfiliado,
                                percentual_comissao: 10.00,
                                status: 'ativo'
                            })
                            .select()
                            .single();

                        if (createError) {
                            console.error('Erro ao criar afiliado diretamente:', createError);
                            setError('Erro de permissão. Execute o script verify_afiliados_data.sql no Supabase SQL Editor para verificar os dados.');
                            return;
                        }

                        afiliado = novoAfiliado;
                        console.log('Afiliado criado diretamente:', afiliado);
                    } catch (directCreateError) {
                        console.error('Erro ao criar afiliado diretamente:', directCreateError);
                        setError('Erro de permissão. Execute o script verify_afiliados_data.sql no Supabase SQL Editor para verificar os dados.');
                        return;
                    }
                } else {
                    setError('Erro ao carregar dados do afiliado: ' + afiliadoError.message);
                    return;
                }
            }

            // Buscar estatísticas do afiliado
            console.log('Buscando estatísticas para afiliado:', afiliado.id);
            const { data: stats, error: statsError } = await supabase
                .from('indicacoes_afiliados')
                .select('status, data_indicacao, data_conversao, valor_comissao')
                .eq('afiliado_id', afiliado.id);

            console.log('Estatísticas encontradas:', stats);
            console.log('Erro das estatísticas:', statsError);

            if (statsError) {
                console.error('Erro ao buscar estatísticas:', statsError);
                // Não bloquear por erro de estatísticas, continuar com dados vazios
            }

            // Calcular estatísticas
            const totalIndicacoes = stats?.length || 0;
            const totalConversoes = stats?.filter(s => s.status === 'concluida').length || 0;
            const totalComissoes = stats?.reduce((sum, s) => sum + (s.valor_comissao || 0), 0) || 0;

            // Estatísticas do mês atual
            const agora = new Date();
            const inicioMesAtual = new Date(agora.getFullYear(), agora.getMonth(), 1);
            const inicioMesAnterior = new Date(agora.getFullYear(), agora.getMonth() - 1, 1);
            const fimMesAnterior = new Date(agora.getFullYear(), agora.getMonth(), 0);

            const indicacoesMesAtual = stats?.filter(s =>
                new Date(s.data_indicacao) >= inicioMesAtual
            ).length || 0;

            const conversoesMesAtual = stats?.filter(s =>
                s.status === 'concluida' &&
                s.data_conversao &&
                new Date(s.data_conversao) >= inicioMesAtual
            ).length || 0;

            const comissoesMesAtual = stats?.filter(s =>
                new Date(s.data_indicacao) >= inicioMesAtual
            ).reduce((sum, s) => sum + (s.valor_comissao || 0), 0) || 0;

            const indicacoesMesAnterior = stats?.filter(s => {
                const data = new Date(s.data_indicacao);
                return data >= inicioMesAnterior && data <= fimMesAnterior;
            }).length || 0;

            const conversoesMesAnterior = stats?.filter(s => {
                if (s.status !== 'concluida' || !s.data_conversao) return false;
                const data = new Date(s.data_conversao);
                return data >= inicioMesAnterior && data <= fimMesAnterior;
            }).length || 0;

            const comissoesMesAnterior = stats?.filter(s => {
                const data = new Date(s.data_indicacao);
                return data >= inicioMesAnterior && data <= fimMesAnterior;
            }).reduce((sum, s) => sum + (s.valor_comissao || 0), 0) || 0;

            // Montar dados do afiliado
            const afiliadoData: AfiliadoData = {
                id: afiliado.id,
                corretor_id: afiliado.corretor_id,
                nome_corretor: corretor.nome,
                email_corretor: corretor.email,
                codigo_afiliado: afiliado.codigo_afiliado,
                link_afiliado: afiliado.link_afiliado,
                percentual_comissao: afiliado.percentual_comissao,
                status: afiliado.status,
                total_indicacoes: totalIndicacoes,
                total_conversoes: totalConversoes,
                total_comissoes: totalComissoes,
                data_cadastro: afiliado.data_cadastro,
                indicacoes_mes_atual: indicacoesMesAtual,
                conversoes_mes_atual: conversoesMesAtual,
                comissoes_mes_atual: comissoesMesAtual,
                indicacoes_mes_anterior: indicacoesMesAnterior,
                conversoes_mes_anterior: conversoesMesAnterior,
                comissoes_mes_anterior: comissoesMesAnterior
            };

            console.log('Dados do afiliado carregados com sucesso:', afiliadoData);
            setAfiliadoData(afiliadoData);
        } catch (err) {
            console.error('Erro ao carregar dados do afiliado:', err);
            setError('Erro ao carregar dados do afiliado');
        } finally {
            setLoading(false);
        }
    };

    const loadIndicacoes = async () => {
        try {
            if (!afiliadoData) return;

            // Buscar indicações do afiliado
            let query = supabase
                .from('indicacoes_afiliados')
                .select(`
          id,
          afiliado_id,
          email_indicado,
          nome_indicado,
          telefone_indicado,
          status,
          data_indicacao,
          data_conversao,
          valor_comissao,
          observacoes,
          corretor_indicado_id,
          corretores!corretor_indicado_id (
            nome,
            email
          )
        `)
                .eq('afiliado_id', afiliadoData.id)
                .order('data_indicacao', { ascending: false });

            // Aplicar filtro de status
            if (filterStatus !== 'todos') {
                query = query.eq('status', filterStatus);
            }

            const { data: indicacoesData, error } = await query;

            if (error) {
                console.error('Erro ao buscar indicações:', error);
                setError('Erro ao carregar indicações');
                return;
            }

            // Transformar dados para o formato esperado
            const indicacoes: IndicacaoData[] = indicacoesData?.map(ind => ({
                id: ind.id,
                afiliado_id: ind.afiliado_id,
                codigo_afiliado: afiliadoData.codigo_afiliado,
                nome_afiliado: afiliadoData.nome_corretor,
                email_indicado: ind.email_indicado,
                nome_indicado: ind.nome_indicado,
                telefone_indicado: ind.telefone_indicado,
                status: ind.status,
                data_indicacao: ind.data_indicacao,
                data_conversao: ind.data_conversao,
                valor_comissao: ind.valor_comissao,
                observacoes: ind.observacoes,
                nome_corretor_indicado: (ind.corretores as any)?.nome,
                email_corretor_indicado: (ind.corretores as any)?.email
            })) || [];

            setIndicacoes(indicacoes);
        } catch (err) {
            console.error('Erro ao carregar indicações:', err);
            setError('Erro ao carregar indicações');
        }
    };

    const copyLinkToClipboard = async () => {
        if (afiliadoData?.link_afiliado) {
            try {
                await navigator.clipboard.writeText(afiliadoData.link_afiliado);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            } catch (err) {
                console.error('Erro ao copiar link:', err);
            }
        }
    };

    const copyLink = () => {
        copyLinkToClipboard();
    };

    const shareLink = async () => {
        if (afiliadoData?.link_afiliado) {
            try {
                if (navigator.share) {
                    await navigator.share({
                        title: 'Cadastre-se na Latera',
                        text: 'Venha fazer parte da melhor plataforma de corretores de imóveis!',
                        url: afiliadoData.link_afiliado
                    });
                } else {
                    // Fallback para navegadores que não suportam Web Share API
                    await navigator.clipboard.writeText(afiliadoData.link_afiliado);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                }
            } catch (err) {
                console.error('Erro ao compartilhar:', err);
            }
        }
    };

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(value);
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'concluida':
                return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400';
            case 'pendente':
                return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400';
            case 'expirada':
                return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400';
            default:
                return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-400';
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'concluida':
                return <CheckCircle size={16} />;
            case 'pendente':
                return <Clock size={16} />;
            case 'expirada':
                return <AlertCircle size={16} />;
            default:
                return <Clock size={16} />;
        }
    };

    const getStatusText = (status: string) => {
        switch (status) {
            case 'concluida':
                return 'Concluída';
            case 'pendente':
                return 'Pendente';
            case 'expirada':
                return 'Expirada';
            default:
                return status;
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64 dark:bg-gray-900">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="p-6 dark:bg-gray-900 min-h-screen">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Sistema de Afiliados</h1>
                    <p className="text-gray-600 dark:text-gray-400">Gerencie suas indicações e comissões</p>
                </div>
                <div className="flex gap-3">
                    <Button
                        variant="btn_add"
                        onClick={copyLinkToClipboard}
                        className="flex items-center gap-2"
                    >
                        <Copy size={20} />
                        {copied ? 'Copiado!' : 'Copiar Link'}
                    </Button>
                    <Button
                        variant="btn_add"
                        onClick={shareLink}
                        className="flex items-center gap-2"
                    >
                        <Share2 size={20} />
                        Compartilhar
                    </Button>
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6 flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
                    <span className="text-red-800 dark:text-red-400">{error}</span>
                </div>
            )}

            {/* Link de Afiliado */}
            {afiliadoData && (
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                                <Link2 size={20} className="text-blue-600 dark:text-blue-400" />
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Seu Link de Afiliado</h3>
                                <p className="text-sm text-gray-600 dark:text-gray-400">Código: {afiliadoData.codigo_afiliado}</p>
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="text-sm text-gray-600 dark:text-gray-400">Percentual de Comissão</div>
                            <div className="text-xl font-bold text-green-600 dark:text-green-400">{afiliadoData.percentual_comissao}%</div>
                        </div>
                    </div>

                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-4">
                        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-2">
                            <ExternalLink size={16} />
                            <span>Link de Indicação:</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div
                                className="flex-1 text-sm font-mono text-gray-900 dark:text-gray-100 break-all bg-white dark:bg-gray-800 p-2 rounded border cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                onClick={() => copyLink()}
                                title="Clique para copiar o link"
                            >
                                {afiliadoData.link_afiliado}
                            </div>
                            <button
                                onClick={() => copyLink()}
                                className={`flex items-center gap-1 px-3 py-2 text-white text-sm rounded-lg transition-colors ${copied
                                    ? 'bg-green-600 hover:bg-green-700'
                                    : 'bg-blue-600 hover:bg-blue-700'
                                    }`}
                                title={copied ? "Link copiado!" : "Copiar link"}
                            >
                                {copied ? <CheckCircle size={16} /> : <Copy size={16} />}
                                {copied ? 'Copiado!' : 'Copiar'}
                            </button>
                        </div>
                        {copied && (
                            <div className="mt-2 text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                                <CheckCircle size={12} />
                                Link copiado.
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Estatísticas */}
            {afiliadoData && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                    {/* Total de Indicações */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                        <div className="flex items-center">
                            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                                <UserPlus size={20} className="text-blue-600 dark:text-blue-400" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total de Indicações</p>
                                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{afiliadoData.total_indicacoes}</p>
                            </div>
                        </div>
                    </div>

                    {/* Total de Clientes Ativos */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                        <div className="flex items-center">
                            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                                <CheckCircle size={20} className="text-green-600 dark:text-green-400" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Clientes Ativos</p>
                                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{afiliadoData.total_conversoes}</p>
                            </div>
                        </div>
                    </div>

                    {/* Taxa de Conversão */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                        <div className="flex items-center">
                            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                                <TrendingUp size={20} className="text-purple-600 dark:text-purple-400" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Taxa de Conversão</p>
                                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                                    {afiliadoData.total_indicacoes > 0
                                        ? Math.round((afiliadoData.total_conversoes / afiliadoData.total_indicacoes) * 100)
                                        : 0}%
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Total de Comissões */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                        <div className="flex items-center">
                            <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                                <DollarSign size={20} className="text-orange-600 dark:text-orange-400" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total de Comissões</p>
                                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{formatCurrency(afiliadoData.total_comissoes)}</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}


            {/* Lista de Indicações */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex justify-between items-center">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Histórico de Indicações</h3>
                        <div className="flex items-center gap-2">
                            <Filter size={16} className="text-gray-500 dark:text-gray-400" />
                            <select
                                value={filterStatus}
                                onChange={(e) => setFilterStatus(e.target.value)}
                                className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                            >
                                <option value="todos">Todos</option>
                                <option value="pendente">Pendentes</option>
                                <option value="concluida">Concluídas</option>
                                <option value="expirada">Expiradas</option>
                            </select>
                        </div>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Pessoa Indicada
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Status
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Data da Indicação
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Data da Conversão
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Comissão
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {indicacoes.map((indicacao) => (
                                <tr key={indicacao.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div>
                                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                                {indicacao.nome_indicado || 'Nome não informado'}
                                            </div>
                                            <div className="text-sm text-gray-500 dark:text-gray-400">
                                                {indicacao.email_indicado}
                                            </div>
                                            {indicacao.telefone_indicado && (
                                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                                    {indicacao.telefone_indicado}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(indicacao.status)}`}>
                                            {getStatusIcon(indicacao.status)}
                                            {getStatusText(indicacao.status)}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                        {formatDate(indicacao.data_indicacao)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                        {indicacao.data_conversao ? formatDate(indicacao.data_conversao) : '-'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                                        {indicacao.valor_comissao > 0 ? formatCurrency(indicacao.valor_comissao) : '-'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {indicacoes.length === 0 && (
                    <div className="text-center py-12">
                        <Users size={48} className="mx-auto text-gray-400 dark:text-gray-600 mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                            Nenhuma indicação encontrada
                        </h3>
                        <p className="text-gray-500 dark:text-gray-400">
                            {filterStatus === 'todos'
                                ? 'Você ainda não fez nenhuma indicação.'
                                : `Nenhuma indicação com status "${getStatusText(filterStatus)}" encontrada.`
                            }
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}
